import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';

// Premium media gallery — images + videos, keyboard, swipe, dots, fade-slide.
// Videos: muted, controls, auto-pause when leaving.
export default function MediaGallery({ items = [], className = '' }) {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1); // 1 forward, -1 back
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef(null);
  const touchStartX = useRef(null);

  const count = items.length;
  const current = items[index];

  const go = (next) => {
    const n = (next + count) % count;
    setDir(n > index || (index === count - 1 && n === 0) ? 1 : -1);
    setIndex(n);
  };
  const prev = () => go(index - 1);
  const next = () => go(index + 1);

  // Keyboard arrows
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === ' ' && current?.type === 'video') {
        e.preventDefault();
        togglePlay();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, count, current]);

  // Pause & reset video on index change
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = 0;
    setPlaying(false);
    setProgress(0);
    setVideoError(false);
  }, [index]);

  // Track video progress
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      if (v.duration > 0) setProgress((v.currentTime / v.duration) * 100);
    };
    const onEnd = () => { setPlaying(false); setProgress(100); };
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('ended', onEnd);
    return () => {
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('ended', onEnd);
    };
  }, [current?.src]);

  // Pause when tab/section not visible
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden && videoRef.current) {
        videoRef.current.pause();
        setPlaying(false);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      const p = v.play();
      if (p && typeof p.then === 'function') {
        p.then(() => setPlaying(true)).catch(() => setPlaying(false));
      } else {
        setPlaying(true);
      }
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const seek = (e) => {
    const v = videoRef.current;
    if (!v || !v.duration || e.clientX == null) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    v.currentTime = ratio * v.duration;
    setProgress(ratio * 100);
  };

  // Touch swipe
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) { if (dx < 0) next(); else prev(); }
    touchStartX.current = null;
  };

  if (!count) return null;

  // Default object-fit: contain so full realizations (doors, windows, glass
  // installations) are always visible without excessive cropping.
  const fitClass = (item) =>
    item.fit === 'cover' ? 'object-cover' : 'object-contain';

  return (
    <div className={`w-full max-w-[1200px] mx-auto ${className}`}>
      {/* Media viewport — compact premium size */}
      <div
        className="hero-media relative w-full h-[260px] sm:h-[380px] md:h-[430px] lg:h-[520px] overflow-hidden rounded-2xl border border-white/10"
        style={{ boxShadow: 'var(--shadow-elev)', background: '#0A0A0A' }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <AnimatePresence initial={false} custom={dir} mode="popLayout">
          <motion.div
            key={index}
            custom={dir}
            initial={{ opacity: 0, x: dir * 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -dir * 30 }}
            transition={{ duration: 0.55, ease: [0.2, 0.8, 0.2, 1] }}
            className="absolute inset-0 flex items-center justify-center"
            style={{ background: '#0A0A0A' }}
          >
            {current.type === 'image' ? (
              <img
                src={current.src}
                alt={current.alt || `Réalisation ${index + 1}`}
                className={`w-full h-full ${fitClass(current)} select-none`}
                draggable={false}
              />
            ) : (
              <>
                <video
                  ref={videoRef}
                  src={`${current.src}#t=0.1`}
                  poster={current.poster}
                  playsInline
                  muted
                  preload="metadata"
                  onError={() => setVideoError(true)}
                  className={`w-full h-full ${fitClass(current)}`}
                />
                {videoError && (
                  <div className="absolute inset-0 flex items-center justify-center text-white/60 font-mono text-[10px] tracking-[0.22em] uppercase text-center px-6">
                    Vidéo indisponible
                  </div>
                )}
              </>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Type badge */}
        <div className="absolute left-4 top-4 z-10 font-mono text-[10px] tracking-[0.24em] uppercase text-white/85 glass px-3 py-1">
          {current.type === 'video' ? 'Vidéo' : 'Photo'} · {String(index + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}
        </div>

        {/* Prev / Next arrows */}
        {count > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              aria-label="Média précédent"
              className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 z-10 h-9 w-9 md:h-10 md:w-10 rounded-full flex items-center justify-center border border-white/20 backdrop-blur-md bg-black/45 hover:bg-white hover:text-black text-white transition-colors"
            >
              <ChevronLeft size={16} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Média suivant"
              className="absolute right-3 md:right-4 top-1/2 -translate-y-1/2 z-10 h-9 w-9 md:h-10 md:w-10 rounded-full flex items-center justify-center border border-white/20 backdrop-blur-md bg-black/45 hover:bg-white hover:text-black text-white transition-colors"
            >
              <ChevronRight size={16} strokeWidth={1.5} />
            </button>
          </>
        )}

        {/* Video controls */}
        {current.type === 'video' && (
          <div className="absolute inset-x-0 bottom-0 z-10 p-4 md:p-5 flex items-center gap-3">
            <button
              type="button"
              onClick={togglePlay}
              aria-label={playing ? 'Pause' : 'Lecture'}
              className="h-10 w-10 rounded-full flex items-center justify-center border border-white/25 backdrop-blur-md bg-black/45 hover:bg-white hover:text-black text-white transition-colors shrink-0"
            >
              {playing ? <Pause size={16} strokeWidth={1.5} /> : <Play size={16} strokeWidth={1.5} className="translate-x-[1px]" />}
            </button>
            <button
              type="button"
              onClick={seek}
              aria-label="Barre de progression"
              className="flex-1 h-9 flex items-center group"
            >
              <div className="w-full h-[3px] bg-white/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white transition-[width] duration-150"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </button>
          </div>
        )}
      </div>

      {/* Dots indicator */}
      {count > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          {items.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => go(i)}
              aria-label={`Aller au média ${i + 1}`}
              className="group h-6 px-1 flex items-center"
            >
              <span
                className={`block rounded-full transition-all duration-300 ${
                  i === index
                    ? 'w-8 h-[3px] bg-white'
                    : 'w-2 h-2 bg-white/25 group-hover:bg-white/60'
                }`}
                style={{ background: i === index ? 'var(--fg)' : 'rgba(var(--fg-rgb), 0.25)' }}
              />
            </button>
          ))}
        </div>
      )}

      {/* Meta strip */}
      <div className="mt-3 flex items-center justify-between font-mono text-[10px] tracking-[0.22em] uppercase text-white/50">
        <span>{String(index + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}</span>
        <span className="hidden sm:inline">← → pour naviguer</span>
      </div>
    </div>
  );
}
